import { useState, type ComponentType, type SVGProps } from 'react';
import type { AppRecord } from '../lib/types';
import { cn } from '../lib/cn';
import {
  Activity,
  BarChart3,
  Briefcase,
  ClipboardList,
  Clock,
  ExternalLink,
  FileBarChart,
  LayoutGrid,
  Lock,
  Mail,
  Pin,
  PinOff,
  PieChart,
  ShieldAlert,
} from 'lucide-react';

type AppTileProps = {
  app: AppRecord;
  pinned: boolean;
  onTogglePin?: (app: AppRecord) => void;
  variant?: 'pinned' | 'list';
  busy?: boolean;
  /** Called when a locked tile is clicked. If provided, replaces the default
   * "open URL" behaviour for apps where canAccess=false. */
  onRequestAccess?: (app: AppRecord) => void;
  /** When true, all mutation actions (pin, request-access modal trigger) are
   * rendered visually but disabled. Used by admin "View as" preview so the
   * admin can see exactly what the target user sees without being able to
   * submit on the user's behalf. */
  preview?: boolean;
};

const formatLaunchDate = (iso: string): string => {
  const d = new Date(iso);
  const now = Date.now();
  const ms = d.getTime() - now;
  if (ms <= 0) return 'launching today';
  const days = Math.ceil(ms / (1000 * 60 * 60 * 24));
  if (days === 1) return 'launches tomorrow';
  if (days < 14) return `launches in ${days} days`;
  if (days < 60) return `launches in ${Math.ceil(days / 7)} weeks`;
  return `launches ${d.toLocaleDateString(undefined, { month: 'short', year: 'numeric' })}`;
};

// The seeded "iconUrl" for every internal app currently points at the company
// wordmark — a wide horizontal logo. Forcing it into a square tile renders it
// as a tiny sliver. Per-app icons land in step 5 (admin upload). Until then,
// the wordmark path is treated as "no icon" and we fall through to a curated
// Lucide icon mapped by app name (Map below) or initials as last resort.
const PLACEHOLDER_ICON = '/brand/alphadirect-logo-orange.svg';

// Tailwind needs these as literal class names to keep them in the build.
const CATEGORY_TINTS: Record<string, string> = {
  'IT': 'bg-blue-100 text-blue-700',
  'Analytics': 'bg-purple-100 text-purple-700',
  'Operations': 'bg-amber-100 text-amber-800',
  'Claims': 'bg-rose-100 text-rose-700',
  'Risk': 'bg-emerald-100 text-emerald-700',
  'Core / Ops': 'bg-brand-orange-pale text-brand-orange-dark',
  'Productivity': 'bg-indigo-100 text-indigo-700',
};
const FALLBACK_TINT = 'bg-brand-navy-pale text-brand-navy';
const tintFor = (cat: string) => CATEGORY_TINTS[cat] ?? FALLBACK_TINT;

type LucideIcon = ComponentType<SVGProps<SVGSVGElement>>;

// Curated per-app icons. New seed apps without a hit fall through to initials.
// Match by case-insensitive name; a substring match keeps small renames safe.
const APP_ICONS: Array<{ match: RegExp; icon: LucideIcon }> = [
  { match: /infra/i, icon: Activity },
  { match: /reporting/i, icon: BarChart3 },
  { match: /sla|email/i, icon: Mail },
  { match: /claims/i, icon: ClipboardList },
  { match: /graphite|risk/i, icon: ShieldAlert },
  { match: /alphafe|core|ops/i, icon: Briefcase },
  { match: /microsoft|m365|office/i, icon: LayoutGrid },
  { match: /power\s?bi|bi\b/i, icon: PieChart },
  { match: /dashboard|analytics/i, icon: FileBarChart },
];

const iconFor = (name: string): LucideIcon | null => {
  for (const { match, icon } of APP_ICONS) {
    if (match.test(name)) return icon;
  }
  return null;
};

const initialsOf = (name: string) =>
  name
    .split(/\s+/)
    .map((s) => s[0] ?? '')
    .join('')
    .slice(0, 2)
    .toUpperCase() || '·';

type IconProps = { app: AppRecord; size: 'sm' | 'md' };

const AppIcon = ({ app, size }: IconProps) => {
  const [imgFailed, setImgFailed] = useState(false);
  const useImage =
    app.iconUrl && app.iconUrl !== PLACEHOLDER_ICON && !imgFailed;

  const box =
    size === 'sm'
      ? 'h-10 w-10 rounded-lg text-xs'
      : 'h-12 w-12 rounded-xl text-sm';

  if (useImage) {
    return (
      <div className={cn('grid place-items-center bg-bg-secondary', box)}>
        <img
          src={app.iconUrl ?? ''}
          alt=""
          className="h-2/3 w-2/3 object-contain"
          loading="lazy"
          onError={() => setImgFailed(true)}
        />
      </div>
    );
  }

  const Icon = iconFor(app.name);
  if (Icon) {
    return (
      <div className={cn('grid place-items-center', box, tintFor(app.category))}>
        <Icon className={cn(size === 'sm' ? 'h-5 w-5' : 'h-6 w-6')} aria-hidden="true" />
      </div>
    );
  }

  return (
    <div className={cn('grid place-items-center font-semibold', box, tintFor(app.category))}>
      {initialsOf(app.name)}
    </div>
  );
};

export const AppTile = ({
  app,
  pinned,
  onTogglePin,
  variant = 'list',
  busy,
  onRequestAccess,
  preview,
}: AppTileProps) => {
  const comingSoon = app.status === 'COMING_SOON';
  const locked = !comingSoon && !app.canAccess;
  const previewTitle = preview
    ? 'Disabled while previewing as another user'
    : undefined;

  // Coming-soon: render <div> (no action). Locked: <button> → request modal.
  // Otherwise: <a> → opens the app URL in a new tab. In preview mode, the
  // locked button still renders so the admin can see the layout, but it's
  // disabled (no modal opens).
  const handleLockedClick = () => {
    if (preview) return;
    onRequestAccess?.(app);
  };

  if (variant === 'pinned') {
    const innerPinned = (
      <>
        <div className="relative">
          <AppIcon app={app} size="md" />
          {comingSoon && (
            <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full border border-bg-primary bg-status-warning text-white">
              <Clock className="h-2.5 w-2.5" aria-hidden="true" />
            </span>
          )}
          {locked && (
            <span className="absolute -bottom-1 -right-1 grid h-5 w-5 place-items-center rounded-full border border-bg-primary bg-text-tertiary text-bg-primary">
              <Lock className="h-2.5 w-2.5" aria-hidden="true" />
            </span>
          )}
        </div>
        <div
          className={cn(
            'line-clamp-2 text-[11px] font-medium leading-tight',
            locked || comingSoon ? 'text-text-tertiary' : 'text-text-primary',
          )}
        >
          {app.name}
        </div>
      </>
    );
    const triggerCls = cn(
      'flex w-full flex-col items-center gap-2 text-center',
      locked && 'cursor-pointer',
      comingSoon && 'cursor-default',
    );

    return (
      <div
        className={cn(
          'group relative flex flex-col items-center rounded-lg border bg-bg-primary p-3 transition-colors',
          locked || comingSoon
            ? 'border-dashed border-line hover:border-line-strong'
            : 'border-line hover:border-line-strong',
        )}
      >
        {comingSoon ? (
          <div className={triggerCls} aria-label={`${app.name} — coming soon`}>
            {innerPinned}
          </div>
        ) : locked ? (
          <button
            type="button"
            onClick={handleLockedClick}
            disabled={preview}
            title={previewTitle}
            aria-label={`Request access to ${app.name}`}
            className={cn(triggerCls, preview && 'cursor-not-allowed opacity-70')}
          >
            {innerPinned}
          </button>
        ) : (
          <a
            href={app.url}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={`Open ${app.name}`}
            className={triggerCls}
          >
            {innerPinned}
          </a>
        )}
        {onTogglePin && !locked && !comingSoon && (
          <button
            type="button"
            onClick={() => !preview && onTogglePin(app)}
            disabled={busy || preview}
            title={previewTitle}
            aria-label={pinned ? `Unpin ${app.name}` : `Pin ${app.name}`}
            className={cn(
              'absolute right-0.5 top-0.5 grid h-11 w-11 place-items-center rounded-md text-text-tertiary transition-opacity hover:bg-bg-secondary hover:text-text-primary focus-visible:opacity-100',
              'md:opacity-0 md:group-hover:opacity-100',
              (busy || preview) && 'cursor-not-allowed opacity-50',
            )}
          >
            {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
          </button>
        )}
      </div>
    );
  }

  const triggerCls = cn(
    'flex min-w-0 flex-1 items-center gap-3 text-left',
    locked && 'cursor-pointer',
    comingSoon && 'cursor-default',
  );
  const innerList = (
    <>
      <div className="relative">
        <AppIcon app={app} size="sm" />
        {comingSoon && (
          <span className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full border border-bg-primary bg-status-warning text-white">
            <Clock className="h-2 w-2" aria-hidden="true" />
          </span>
        )}
        {locked && (
          <span className="absolute -bottom-1 -right-1 grid h-4 w-4 place-items-center rounded-full border border-bg-primary bg-text-tertiary text-bg-primary">
            <Lock className="h-2 w-2" aria-hidden="true" />
          </span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <div
            className={cn(
              'truncate text-sm font-semibold',
              locked || comingSoon ? 'text-text-secondary' : 'text-text-primary',
            )}
          >
            {app.name}
          </div>
          {comingSoon ? (
            <span className="shrink-0 rounded-full bg-status-warning/15 px-2 py-0.5 text-[10px] font-medium text-status-warning">
              Coming soon
            </span>
          ) : locked ? (
            <span className="shrink-0 rounded-full bg-bg-secondary px-2 py-0.5 text-[10px] font-medium text-text-tertiary">
              Locked
            </span>
          ) : (
            <ExternalLink className="h-3 w-3 shrink-0 text-text-tertiary opacity-0 transition-opacity group-hover:opacity-100" />
          )}
        </div>
        {(app.description || comingSoon) && (
          <div className="truncate text-xs text-text-tertiary">
            {comingSoon
              ? app.launchDate
                ? formatLaunchDate(app.launchDate)
                : 'in development'
              : locked
                ? 'Click to request access'
                : app.description}
          </div>
        )}
      </div>
      {app.ssoEnabled && !locked && !comingSoon && (
        <span className="hidden shrink-0 rounded-full bg-brand-navy-pale px-2 py-0.5 text-[10px] font-medium text-brand-navy md:inline-block">
          SSO
        </span>
      )}
    </>
  );

  return (
    <div
      className={cn(
        'group flex items-center gap-3 rounded-lg border bg-bg-primary p-3 transition-colors',
        locked || comingSoon
          ? 'border-dashed border-line hover:border-line-strong'
          : 'border-line hover:border-line-strong',
      )}
    >
      {comingSoon ? (
        <div className={triggerCls} aria-label={`${app.name} — coming soon`}>
          {innerList}
        </div>
      ) : locked ? (
        <button
          type="button"
          onClick={handleLockedClick}
          disabled={preview}
          title={previewTitle}
          aria-label={`Request access to ${app.name}`}
          className={cn(triggerCls, preview && 'cursor-not-allowed opacity-70')}
        >
          {innerList}
        </button>
      ) : (
        <a
          href={app.url}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={`Open ${app.name}`}
          className={triggerCls}
        >
          {innerList}
        </a>
      )}
      {onTogglePin && !locked && !comingSoon && (
        <button
          type="button"
          onClick={() => !preview && onTogglePin(app)}
          disabled={busy || preview}
          title={previewTitle}
          aria-label={pinned ? `Unpin ${app.name}` : `Pin ${app.name}`}
          className={cn(
            'grid h-11 w-11 shrink-0 place-items-center rounded-md text-text-tertiary transition-colors hover:bg-bg-secondary hover:text-text-primary',
            pinned && 'text-brand-orange hover:text-brand-orange-dark',
            (busy || preview) && 'cursor-not-allowed opacity-50',
          )}
        >
          {pinned ? <PinOff className="h-4 w-4" /> : <Pin className="h-4 w-4" />}
        </button>
      )}
    </div>
  );
};
